// pages/TeamProjectCollaborationPage.tsx
import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import axios from 'axios';
import {
  Vote as VoteIcon,
  Plus,
  Clock,
  Users,
  CheckCircle2,
  X,
  AlertCircle,
  TrendingUp,
  User,
  Loader2,
  Calendar,
  BarChart3,
  Lock,
  Trash2,
  Edit,
  Gauge,
  Kanban,
  List,
  FileText,
  ArrowLeft,
  Tag,
  Hash,
  Play
} from 'lucide-react';
import { RootState } from '../store/store';
import { selectUser } from '../store/slices/authSlice';

const API_BASE = '/api';

interface ProjectData {
  _id: string;
  title: string;
  description: string;
  creator: { _id: string; name: string; email: string };
}

interface TeamData {
  _id: string;
  name: string;
  description?: string;
  members: { _id: string; name: string; email: string }[];
  creator: { _id: string; name: string; email: string };
}

interface Task {
  _id: string;
  title: string;
  description: string;
  status: 'todo' | 'in-progress' | 'review' | 'done' | 'blocked' | 'archived';
  priority: 'low' | 'medium' | 'high' | 'urgent';
  assignee: { _id: string; name: string } | null;
  deadline?: string;
  tags: string[];
  progress: number;
  isOverdue: boolean;
  estimatedDuration?: number;
  createdBy?: string | { _id: string; name: string; email: string };
  subTasks: {
    title: string;
    status: string;
    assignee: { _id: string; name: string } | null;
  }[];
}

interface VoteOption {
  text: string;
  order: number;
  hours?: number;
}

interface VoteData {
  _id: string;
  title: string;
  description: string;
  type: 'simple' | 'multiple' | 'weighted' | 'ranking' | 'time_estimation';
  options: VoteOption[];
  projectId: string;
  teamId?: string;
  creator: { _id: string; name: string; email: string };
  status: 'active' | 'closed' | 'cancelled';
  votes: Array<{
    userId: { _id: string; name: string; email: string };
    selectedOptions: Array<{
      optionIndex: number;
      rank?: number;
      weight?: number;
    }>;
    votedAt: string;
  }>;
  results: {
    totalVotes: number;
    participationRate: number;
    optionResults: Array<{
      optionIndex: number;
      votes: number;
      percentage: number;
    }>;
    winner: {
      optionIndex: number;
      optionText: string;
      isTie: boolean;
    };
    averageHours?: number;
  };
  resultsVisibility: 'hidden' | 'realtime' | 'after_close';
  endDate: string;
  startDate: string;
  closedAt?: string;
  isAnonymous: boolean;
  linkedTaskId?: string;
  estimatedHours?: number;
  userHasVoted?: boolean;
  canVote?: { canVote: boolean; reason?: string };
}

const TeamProjectCollaborationPage: React.FC = () => {
  const { projectId, teamId } = useParams<{ projectId: string; teamId: string }>();
  const navigate = useNavigate();
  const user = useSelector((state: RootState) => selectUser(state));

  const [activeTab, setActiveTab] = useState<'votes' | 'tasks'>('votes');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Project & Team data
  const [project, setProject] = useState<ProjectData | null>(null);
  const [team, setTeam] = useState<TeamData | null>(null);

  // Votes state
  const [votes, setVotes] = useState<VoteData[]>([]);
  const [showCreateVoteModal, setShowCreateVoteModal] = useState(false);
  const [selectedVote, setSelectedVote] = useState<VoteData | null>(null);
  const [showVoteModal, setShowVoteModal] = useState(false);
  const [selectedOptions, setSelectedOptions] = useState<number[]>([]);
  const [voteFormData, setVoteFormData] = useState({
    title: '',
    description: '',
    type: 'simple' as 'simple' | 'multiple',
    options: [{ text: '', order: 0 }, { text: '', order: 1 }],
    endDate: '',
    resultsVisibility: 'after_close' as 'hidden' | 'realtime' | 'after_close',
    allowVoteModification: true,
    isAnonymous: false
  });

  // Tasks state
  const [tasks, setTasks] = useState<Task[]>([]);
  const [showCreateTaskModal, setShowCreateTaskModal] = useState(false);
  const [selectedTask, setSelectedTask] = useState<Task | null>(null);
  const [showTaskModal, setShowTaskModal] = useState(false);
  const [taskFormData, setTaskFormData] = useState({
    title: '',
    description: '',
    priority: 'medium' as 'low' | 'medium' | 'high' | 'urgent',
    deadline: '',
    tags: [] as string[]
  });

  // Time estimation votes
  const [timeEstimationVotes, setTimeEstimationVotes] = useState<Record<string, VoteData | null>>({});
  const [showEstimationModal, setShowEstimationModal] = useState(false);
  const [selectedTaskForEstimation, setSelectedTaskForEstimation] = useState<Task | null>(null);
  const [estimationLoading, setEstimationLoading] = useState(false);
  const [selectedEstimationOptions, setSelectedEstimationOptions] = useState<number[]>([]);
  const [customHours, setCustomHours] = useState<string>('');

  // Fetch project and team
  useEffect(() => {
    const fetchData = async () => {
      if (!projectId || !teamId || !user?._id) return;
      setLoading(true);
      setError(null);
      try {
        // Fetch project
        const projectRes = await axios.get(`${API_BASE}/projects/${projectId}`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        });
        if (projectRes.data.success) {
          setProject(projectRes.data.data);
          // Find team in project teams
          const teams = projectRes.data.data.teams || [];
          const foundTeam = teams.find((t: TeamData) => t._id === teamId);
          if (foundTeam) {
            setTeam(foundTeam);
          } else {
            // Try to fetch team directly
            const teamRes = await axios.get(`${API_BASE}/teams/${teamId}`, {
              headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
            });
            if (teamRes.data.success) {
              setTeam(teamRes.data.data);
            }
          }
        }

        // Fetch votes
        await fetchVotes();
        // Fetch tasks
        await fetchTasks();
      } catch (err: any) {
        setError(err.response?.data?.error || 'Erreur lors du chargement');
        console.error('Erreur chargement:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [projectId, teamId, user?._id]);

  // Fetch votes
  const fetchVotes = async () => {
    if (!projectId) return;
    try {
      const res = await axios.get(`${API_BASE}/votes/project/${projectId}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      setVotes(res.data.data || []);
    } catch (err: any) {
      console.error('Erreur fetch votes:', err);
    }
  };

  // Fetch tasks
  const fetchTasks = async () => {
    if (!teamId) return;
    try {
      const res = await axios.get(`${API_BASE}/tasks/team/${teamId}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      const fetchedTasks = Array.isArray(res.data.data?.tasks) ? res.data.data.tasks : [];
      setTasks(fetchedTasks);
      
      // Fetch time estimation votes for all tasks
      for (const task of fetchedTasks) {
        await fetchTimeEstimationVote(task._id);
      }
    } catch (err: any) {
      console.error('Erreur fetch tasks:', err);
    }
  };

  // Fetch time estimation vote for a task
  const fetchTimeEstimationVote = async (taskId: string) => {
    try {
      const res = await axios.get(`${API_BASE}/votes/task/${taskId}/time-estimation`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      if (res.data.success) {
        setTimeEstimationVotes(prev => ({ ...prev, [taskId]: res.data.data }));
      }
    } catch (err: any) {
      if (err.response?.status !== 404) {
        console.error('Erreur récupération vote estimation:', err);
      }
      setTimeEstimationVotes(prev => ({ ...prev, [taskId]: null }));
    }
  };

  // Create vote
  const handleCreateVote = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!projectId || !user?._id) return;

    if (!voteFormData.title.trim()) {
      setError('Le titre est obligatoire');
      return;
    }
    if (voteFormData.options.length < 2) {
      setError('Au moins 2 options sont requises');
      return;
    }
    if (voteFormData.options.some(opt => !opt.text.trim())) {
      setError('Toutes les options doivent avoir un texte');
      return;
    }
    if (!voteFormData.endDate) {
      setError('La date de fin est obligatoire');
      return;
    }

    try {
      const res = await axios.post(
        `${API_BASE}/votes`,
        {
          ...voteFormData,
          projectId,
          teamId: teamId || undefined,
          options: voteFormData.options.map((opt, idx) => ({ text: opt.text, order: idx }))
        },
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
      );
      if (res.data.success) {
        await fetchVotes();
        setShowCreateVoteModal(false);
        setVoteFormData({
          title: '',
          description: '',
          type: 'simple',
          options: [{ text: '', order: 0 }, { text: '', order: 1 }],
          endDate: '',
          resultsVisibility: 'after_close',
          allowVoteModification: true,
          isAnonymous: false
        });
        setError(null);
      }
    } catch (err: any) {
      setError(err.response?.data?.error || 'Error creating the vote');
    }
  };

  // Submit vote
  const handleSubmitVote = async () => {
    if (!selectedVote || selectedOptions.length === 0) return;
    try {
      await axios.post(
        `${API_BASE}/votes/${selectedVote._id}/vote`,
        { selectedOptions: selectedOptions.map(idx => ({ optionIndex: idx })) },
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
      );
      await fetchVotes();
      setShowVoteModal(false);
      setSelectedVote(null);
      setSelectedOptions([]);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Erreur lors de la soumission du vote');
    }
  };

  // Create task
  const handleCreateTask = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!projectId || !teamId || !user?._id) return;

    if (!taskFormData.title.trim()) {
      setError('Le titre est obligatoire');
      return;
    }

    try {
      const res = await axios.post(
        `${API_BASE}/tasks`,
        {
          ...taskFormData,
          project: projectId,
          team: teamId,
          status: 'todo'
        },
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
      );
      if (res.data.success) {
        await fetchTasks();
        setShowCreateTaskModal(false);
        setTaskFormData({
          title: '',
          description: '',
          priority: 'medium',
          deadline: '',
          tags: []
        });
        setError(null);
      }
    } catch (err: any) {
      setError(err.response?.data?.error || 'Error creating the task');
    }
  };

  // Create time estimation vote
  const createTimeEstimationVote = async (task: Task) => {
    try {
      setEstimationLoading(true);
      setError(null);
      const res = await axios.post(
        `${API_BASE}/votes/task/${task._id}/time-estimation`,
        { endDate: new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString() },
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
      );
      if (res.data.success) {
        await fetchTimeEstimationVote(task._id);
        setSelectedTaskForEstimation(task);
        setShowEstimationModal(true);
      }
    } catch (err: any) {
      setError(err.response?.data?.error || 'Error creating the estimation vote');
    } finally {
      setEstimationLoading(false);
    }
  };

  // Submit time estimation vote
  const submitTimeEstimationVote = async () => {
    if (!selectedTaskForEstimation || (!selectedEstimationOptions.length && !customHours) || !timeEstimationVotes[selectedTaskForEstimation._id]) return;
    try {
      setEstimationLoading(true);
      setError(null);
      const voteId = timeEstimationVotes[selectedTaskForEstimation._id]?._id;
      
      // Si customHours est fourni, l'utiliser, sinon utiliser les options sélectionnées
      const payload: any = {};
      if (customHours && customHours.trim()) {
        const hours = parseFloat(customHours);
        if (isNaN(hours) || hours < 0) {
          setError('Veuillez entrer un nombre d\'heures valide');
          setEstimationLoading(false);
          return;
        }
        payload.customHours = hours;
      } else {
        payload.selectedOptions = selectedEstimationOptions.map(index => ({ optionIndex: index }));
      }
      
      await axios.post(
        `${API_BASE}/votes/${voteId}/vote`,
        payload,
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
      );
      await fetchTimeEstimationVote(selectedTaskForEstimation._id);
      setShowEstimationModal(false);
      setSelectedTaskForEstimation(null);
      setSelectedEstimationOptions([]);
      setCustomHours('');
    } catch (err: any) {
      setError(err.response?.data?.error || 'Erreur lors de la soumission de l\'estimation');
    } finally {
      setEstimationLoading(false);
    }
  };

  // Apply time estimation
  const applyTimeEstimation = async () => {
    if (!selectedTaskForEstimation || !timeEstimationVotes[selectedTaskForEstimation._id]) return;
    try {
      setEstimationLoading(true);
      setError(null);
      const voteId = timeEstimationVotes[selectedTaskForEstimation._id]?._id;
      await axios.post(
        `${API_BASE}/votes/${voteId}/apply-estimation`,
        {},
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
      );
      await fetchTasks();
      await fetchTimeEstimationVote(selectedTaskForEstimation._id);
      setShowEstimationModal(false);
      setSelectedTaskForEstimation(null);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Erreur lors de l\'application de l\'estimation');
    } finally {
      setEstimationLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('fr-FR', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const isVoteActive = (vote: VoteData) => {
    return vote.status === 'active' && new Date(vote.endDate) > new Date();
  };

  const canShowResults = (vote: VoteData) => {
    if (vote.resultsVisibility === 'realtime') return true;
    if (vote.resultsVisibility === 'after_close' && vote.status === 'closed') return true;
    return false;
  };

  const hasValidResults = (vote: VoteData) => {
    return vote.results && vote.results.optionResults && vote.results.optionResults.length > 0;
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'urgent': return 'bg-red-100 text-red-700';
      case 'high': return 'bg-orange-100 text-orange-700';
      case 'medium': return 'bg-yellow-100 text-yellow-700';
      case 'low': return 'bg-green-100 text-green-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'done': return 'bg-green-100 text-green-700';
      case 'in-progress': return 'bg-blue-100 text-blue-700';
      case 'review': return 'bg-purple-100 text-purple-700';
      case 'blocked': return 'bg-red-100 text-red-700';
      case 'archived': return 'bg-gray-100 text-gray-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
      </div>
    );
  }

  if (!project || !team) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
          <p className="text-gray-600">Project or team not found</p>
          <button
            onClick={() => navigate('/projects')}
            className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            Retour aux projets
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 shadow-sm">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate(`/projects/${projectId}`)}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <ArrowLeft className="h-5 w-5 text-gray-600" />
              </button>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">{project.title}</h1>
                <div className="flex items-center gap-4 mt-1">
                  <div className="flex items-center gap-2 text-sm text-gray-600">
                    <Users className="h-4 w-4" />
                    <span>{team.name}</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-gray-600">
                    <User className="h-4 w-4" />
                    <span>{team.members.length} membre{team.members.length > 1 ? 's' : ''}</span>
                  </div>
                </div>
              </div>
              <button
                onClick={() => navigate(`/projects/${projectId}/team/${teamId}/planning-poker`)}
                className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
              >
                <Play className="h-4 w-4" />
                Planning Poker
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex gap-1">
            <button
              onClick={() => setActiveTab('votes')}
              className={`px-6 py-3 font-medium text-sm transition-colors border-b-2 ${
                activeTab === 'votes'
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-gray-600 hover:text-gray-900'
              }`}
            >
              <div className="flex items-center gap-2">
                <VoteIcon className="h-4 w-4" />
                Votes
              </div>
            </button>
            <button
              onClick={() => setActiveTab('tasks')}
              className={`px-6 py-3 font-medium text-sm transition-colors border-b-2 ${
                activeTab === 'tasks'
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-gray-600 hover:text-gray-900'
              }`}
            >
              <div className="flex items-center gap-2">
                <FileText className="h-4 w-4" />
                Tasks
              </div>
            </button>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-6 py-6">
        {error && (
          <div className="mb-6 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg flex items-center gap-2">
            <AlertCircle className="h-5 w-5" />
            <span className="font-medium">{error}</span>
            <button onClick={() => setError(null)} className="ml-auto">
              <X className="h-4 w-4" />
            </button>
          </div>
        )}

        {/* Votes Tab */}
        {activeTab === 'votes' && (
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <h2 className="text-xl font-semibold text-gray-900">Votes du projet</h2>
              <button
                onClick={() => setShowCreateVoteModal(true)}
                className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                <Plus className="h-4 w-4" />
                Create a vote
              </button>
            </div>

            {votes.length === 0 ? (
              <div className="text-center py-12 bg-white rounded-lg border border-gray-200">
                <VoteIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-500">Aucun vote pour le moment</p>
              </div>
            ) : (
              <div className="space-y-4">
                {votes.map((vote) => {
                  const isActive = vote.status === 'active' && isVoteActive(vote);
                  return (
                    <div
                      key={vote._id}
                      className="bg-white border border-gray-200 rounded-lg p-6 hover:shadow-md transition-shadow"
                    >
                      <div className="flex items-start justify-between mb-4">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2">
                            <h3 className="text-lg font-semibold text-gray-900">{vote.title}</h3>
                            {isActive ? (
                              <span className="px-2 py-1 bg-green-100 text-green-700 text-xs font-medium rounded">
                                Actif
                              </span>
                            ) : (
                              <span className="px-2 py-1 bg-gray-100 text-gray-700 text-xs font-medium rounded">
                                Closed
                              </span>
                            )}
                          </div>
                          {vote.description && (
                            <p className="text-sm text-gray-600 mb-3">{vote.description}</p>
                          )}
                          <div className="flex items-center gap-4 text-xs text-gray-500">
                            <div className="flex items-center gap-1">
                              <User className="h-3 w-3" />
                              {vote.creator.name}
                            </div>
                            <div className="flex items-center gap-1">
                              <Clock className="h-3 w-3" />
                              Closes: {formatDate(vote.endDate)}
                            </div>
                            <div className="flex items-center gap-1">
                              <Users className="h-3 w-3" />
                              {vote.results.totalVotes} vote{vote.results.totalVotes > 1 ? 's' : ''}
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          {isActive && (
                            <>
                              {vote.userHasVoted ? (
                                <span className="text-sm text-green-600 flex items-center gap-1">
                                  <CheckCircle2 className="h-4 w-4" />
                                  You voted
                                </span>
                              ) : vote.canVote?.canVote ? (
                                <button
                                  onClick={() => {
                                    setSelectedVote(vote);
                                    setSelectedOptions([]);
                                    setShowVoteModal(true);
                                  }}
                                  className="px-4 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700"
                                >
                                  Voter
                                </button>
                              ) : (
                                <span className="text-sm text-gray-500">{vote.canVote?.reason || 'Vous ne pouvez pas voter'}</span>
                              )}
                            </>
                          )}
                        </div>
                      </div>

                      {/* Options - Ne pas afficher pour les votes time_estimation */}
                      {vote.type !== 'time_estimation' && (
                        <div className="mb-4">
                          <h4 className="text-sm font-medium text-gray-700 mb-2">Options :</h4>
                          <div className="space-y-2">
                            {vote.options.map((option, index) => {
                              const result = vote.results.optionResults.find(r => r.optionIndex === index);
                              const percentage = result?.percentage || 0;
                              const votesCount = result?.votes || 0;
                              return (
                                <div key={index} className="space-y-1">
                                  <div className="flex items-center justify-between">
                                    <span className="text-sm text-gray-800 flex-1">{option.text}</span>
                                    {canShowResults(vote) && (
                                      <div className="flex items-center gap-2 text-xs text-gray-600">
                                        <BarChart3 className="h-3 w-3" />
                                        <span>{votesCount} vote{votesCount > 1 ? 's' : ''}</span>
                                        <span>({percentage.toFixed(1)}%)</span>
                                      </div>
                                    )}
                                  </div>
                                  {canShowResults(vote) && (
                                    <div className="w-full bg-gray-200 rounded-full h-2 overflow-hidden">
                                      <div
                                        className="h-full bg-blue-500"
                                        style={{ width: `${percentage}%` }}
                                      ></div>
                                    </div>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}

                      {/* Affichage de la moyenne pour les votes time_estimation */}
                      {vote.type === 'time_estimation' && canShowResults(vote) && vote.results && (
                        <div className="mb-4">
                          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                            <div className="flex items-center gap-3">
                              <div className="p-2 bg-blue-500 rounded-lg">
                                <Gauge className="h-5 w-5 text-white" />
                              </div>
                              <div className="flex-1">
                                <span className="text-xs font-semibold text-gray-600 uppercase tracking-wide">Estimation moyenne</span>
                                <div className="flex items-center gap-2 mt-1">
                                  {vote.estimatedHours !== undefined && vote.estimatedHours !== null ? (
                                    <>
                                      <span className="text-lg font-bold text-gray-900">
                                        {vote.estimatedHours.toFixed(1)} heures
                                      </span>
                                      <span className="text-sm text-gray-600">
                                        ({Math.ceil(vote.estimatedHours / 8)} jour{Math.ceil(vote.estimatedHours / 8) > 1 ? 's' : ''})
                                      </span>
                                    </>
                                  ) : vote.results.averageHours !== undefined && vote.results.averageHours !== null ? (
                                    <>
                                      <span className="text-lg font-bold text-gray-900">
                                        {vote.results.averageHours.toFixed(1)} heures
                                      </span>
                                      <span className="text-sm text-gray-600">
                                        ({Math.ceil(vote.results.averageHours / 8)} jour{Math.ceil(vote.results.averageHours / 8) > 1 ? 's' : ''})
                                      </span>
                                    </>
                                  ) : (
                                    <span className="text-sm text-gray-600">En attente de votes...</span>
                                  )}
                                </div>
                                <div className="text-xs text-gray-500 mt-1">
                                  {vote.results.totalVotes} vote{vote.results.totalVotes > 1 ? 's' : ''}
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Results */}
                      {canShowResults(vote) && hasValidResults(vote) && (
                        <div className={`mt-4 pt-4 border-t border-gray-200 p-4 rounded-lg ${
                          vote.results.winner.isTie
                            ? 'bg-orange-50 border-orange-200'
                            : 'bg-green-50 border-green-200'
                        }`}>
                          <div className="flex items-center gap-3">
                            <div className={`p-2 rounded-lg ${
                              vote.results.winner.isTie ? 'bg-orange-500' : 'bg-green-500'
                            }`}>
                              <TrendingUp className="h-5 w-5 text-white" />
                            </div>
                            <div className="flex-1">
                              <span className="text-xs font-semibold text-gray-600 uppercase tracking-wide">Result</span>
                              <div className="flex items-center gap-2 mt-1 flex-wrap">
                                {vote.results.winner.isTie ? (
                                  <>
                                    <span className="text-lg font-bold text-orange-700">Tie detected</span>
                                    <span className="px-2 py-0.5 bg-orange-100 text-orange-700 text-xs font-semibold rounded-full">Aucun gagnant</span>
                                  </>
                                ) : (
                                  <>
                                    <span className="text-lg font-bold text-gray-900">{vote.results.winner.optionText}</span>
                                    <span className="px-2 py-0.5 bg-green-100 text-green-700 text-xs font-semibold rounded-full">Gagnant</span>
                                  </>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* Tasks Tab */}
        {activeTab === 'tasks' && (
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <h2 className="text-xl font-semibold text-gray-900">Team tasks</h2>
              <button
                onClick={() => setShowCreateTaskModal(true)}
                className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                <Plus className="h-4 w-4" />
                Create a task
              </button>
            </div>

            {tasks.length === 0 ? (
              <div className="text-center py-12 bg-white rounded-lg border border-gray-200">
                <FileText className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-500">No tasks yet</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {tasks.map((task) => {
                  const estimationVote = timeEstimationVotes[task._id];
                  return (
                    <div
                      key={task._id}
                      className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow cursor-pointer"
                      onClick={() => {
                        setSelectedTask(task);
                        setShowTaskModal(true);
                      }}
                    >
                      <div className="flex items-start justify-between mb-2">
                        <h3 className="font-semibold text-gray-900 flex-1">{task.title}</h3>
                        <span className={`px-2 py-1 rounded text-xs font-semibold ${getPriorityColor(task.priority)}`}>
                          {task.priority}
                        </span>
                      </div>
                      {task.description && (
                        <p className="text-sm text-gray-600 mb-3 line-clamp-2">{task.description}</p>
                      )}
                      <div className="flex items-center gap-2 mb-3 text-xs text-gray-500">
                        <span className={`px-2 py-1 rounded ${getStatusColor(task.status)}`}>
                          {task.status}
                        </span>
                        {task.assignee && (
                          <div className="flex items-center">
                            <div className="w-5 h-5 rounded-full bg-blue-500 text-white text-xs font-bold flex items-center justify-center">
                              {task.assignee.name[0]}
                            </div>
                            <span className="ml-1">{task.assignee.name}</span>
                          </div>
                        )}
                        {task.deadline && (
                          <span className="flex items-center">
                            <Calendar className="h-3 w-3 mr-1" />
                            {formatDate(task.deadline)}
                          </span>
                        )}
                      </div>
                      <div className="flex items-center justify-between mt-3 pt-3 border-t border-gray-200">
                        {estimationVote ? (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedTaskForEstimation(task);
                              setShowEstimationModal(true);
                            }}
                            className="flex items-center gap-1 px-3 py-1 bg-blue-50 text-blue-700 rounded-md text-xs font-medium hover:bg-blue-100"
                          >
                            <Gauge className="h-3 w-3" />
                            Voir estimation
                          </button>
                        ) : (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              createTimeEstimationVote(task);
                            }}
                            disabled={estimationLoading}
                            className="flex items-center gap-1 px-3 py-1 bg-gray-100 text-gray-700 rounded-md text-xs font-medium hover:bg-gray-200 disabled:opacity-50"
                          >
                            <Clock className="h-3 w-3" />
                            {estimationLoading ? 'Creating...' : 'Estimate time'}
                          </button>
                        )}
                        {task.estimatedDuration && task.estimatedDuration > 0 && (
                          <span className="text-xs text-blue-600 flex items-center">
                            <Clock className="h-3 w-3 mr-1" />
                            {task.estimatedDuration} jour{task.estimatedDuration > 1 ? 's' : ''}
                          </span>
                        )}
                      </div>
                      <div className="w-full h-1.5 bg-gray-200 rounded-full overflow-hidden mt-2">
                        <div
                          className="h-full bg-blue-500"
                          style={{ width: `${task.progress}%` }}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Create Vote Modal */}
      {showCreateVoteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b px-6 py-4 flex items-center justify-between">
              <h3 className="text-xl font-semibold">Create a vote</h3>
              <button
                onClick={() => {
                  setShowCreateVoteModal(false);
                  setError(null);
                }}
                className="text-gray-500 hover:text-gray-700"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <form onSubmit={handleCreateVote} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Titre *</label>
                <input
                  type="text"
                  value={voteFormData.title}
                  onChange={e => setVoteFormData({ ...voteFormData, title: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <textarea
                  value={voteFormData.description}
                  onChange={e => setVoteFormData({ ...voteFormData, description: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  rows={3}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Type de vote</label>
                <select
                  value={voteFormData.type}
                  onChange={e => setVoteFormData({ ...voteFormData, type: e.target.value as any })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                >
                  <option value="simple">Vote simple</option>
                  <option value="multiple">Choix multiples</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Options *</label>
                {voteFormData.options.map((option, index) => (
                  <div key={index} className="flex items-center gap-2 mb-2">
                    <input
                      type="text"
                      value={option.text}
                      onChange={e => {
                        const newOptions = [...voteFormData.options];
                        newOptions[index].text = e.target.value;
                        setVoteFormData({ ...voteFormData, options: newOptions });
                      }}
                      placeholder={`Option ${index + 1}`}
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-lg"
                      required
                    />
                    {voteFormData.options.length > 2 && (
                      <button
                        type="button"
                        onClick={() => {
                          const newOptions = voteFormData.options.filter((_, i) => i !== index);
                          setVoteFormData({ ...voteFormData, options: newOptions });
                        }}
                        className="p-2 text-red-500 hover:text-red-700"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    )}
                  </div>
                ))}
                <button
                  type="button"
                  onClick={() => {
                    setVoteFormData({
                      ...voteFormData,
                      options: [...voteFormData.options, { text: '', order: voteFormData.options.length }]
                    });
                  }}
                  className="text-sm text-blue-600 hover:text-blue-700 flex items-center gap-1"
                >
                  <Plus className="h-4 w-4" />
                  Ajouter une option
                </button>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Date de fin *</label>
                <input
                  type="datetime-local"
                  value={voteFormData.endDate}
                  onChange={e => setVoteFormData({ ...voteFormData, endDate: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Results visibility</label>
                <select
                  value={voteFormData.resultsVisibility}
                  onChange={e => setVoteFormData({ ...voteFormData, resultsVisibility: e.target.value as any })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                >
                  <option value="hidden">Hidden until closed</option>
                  <option value="realtime">Real-time</option>
                  <option value="after_close">After closing</option>
                </select>
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="allowModification"
                  checked={voteFormData.allowVoteModification}
                  onChange={e => setVoteFormData({ ...voteFormData, allowVoteModification: e.target.checked })}
                />
                <label htmlFor="allowModification" className="text-sm text-gray-700">Permettre la modification du vote</label>
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="isAnonymous"
                  checked={voteFormData.isAnonymous}
                  onChange={e => setVoteFormData({ ...voteFormData, isAnonymous: e.target.checked })}
                />
                <label htmlFor="isAnonymous" className="text-sm text-gray-700">Vote anonyme</label>
              </div>
              <div className="flex items-center gap-4 pt-4 border-t border-gray-200">
                <button
                  type="submit"
                  className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  Create vote
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowCreateVoteModal(false);
                    setError(null);
                  }}
                  className="flex-1 px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
                >
                  Annuler
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Vote Modal */}
      {showVoteModal && selectedVote && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-lg w-full">
            <div className="sticky top-0 bg-white border-b px-6 py-4 flex items-center justify-between">
              <h3 className="text-xl font-semibold">{selectedVote.title}</h3>
              <button
                onClick={() => {
                  setShowVoteModal(false);
                  setSelectedVote(null);
                  setSelectedOptions([]);
                }}
                className="text-gray-500 hover:text-gray-700"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="p-6 space-y-4">
              {selectedVote.description && (
                <p className="text-sm text-gray-600">{selectedVote.description}</p>
              )}
              <div className="space-y-2">
                {selectedVote.options.map((option, index) => (
                  <label
                    key={index}
                    className={`flex items-center gap-3 p-3 border-2 rounded-lg cursor-pointer transition-colors ${
                      selectedOptions.includes(index)
                        ? 'border-blue-500 bg-blue-50'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <input
                      type={selectedVote.type === 'simple' ? 'radio' : 'checkbox'}
                      name="voteOption"
                      checked={selectedOptions.includes(index)}
                      onChange={(e) => {
                        if (selectedVote.type === 'simple') {
                          setSelectedOptions([index]);
                        } else {
                          if (e.target.checked) {
                            setSelectedOptions([...selectedOptions, index]);
                          } else {
                            setSelectedOptions(selectedOptions.filter(i => i !== index));
                          }
                        }
                      }}
                      className="h-4 w-4 text-blue-600"
                    />
                    <span className="flex-1">{option.text}</span>
                  </label>
                ))}
              </div>
              <div className="flex items-center gap-4 pt-4 border-t border-gray-200">
                <button
                  onClick={handleSubmitVote}
                  disabled={selectedOptions.length === 0}
                  className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
                >
                  Soumettre le vote
                </button>
                <button
                  onClick={() => {
                    setShowVoteModal(false);
                    setSelectedVote(null);
                    setSelectedOptions([]);
                  }}
                  className="flex-1 px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
                >
                  Annuler
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Create Task Modal */}
      {showCreateTaskModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b px-6 py-4 flex items-center justify-between">
              <h3 className="text-xl font-semibold">Create a task</h3>
              <button
                onClick={() => {
                  setShowCreateTaskModal(false);
                  setError(null);
                }}
                className="text-gray-500 hover:text-gray-700"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <form onSubmit={handleCreateTask} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Titre *</label>
                <input
                  type="text"
                  value={taskFormData.title}
                  onChange={e => setTaskFormData({ ...taskFormData, title: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <textarea
                  value={taskFormData.description}
                  onChange={e => setTaskFormData({ ...taskFormData, description: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  rows={4}
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Priority</label>
                  <select
                    value={taskFormData.priority}
                    onChange={e => setTaskFormData({ ...taskFormData, priority: e.target.value as any })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  >
                    <option value="low">Basse</option>
                    <option value="medium">Moyenne</option>
                    <option value="high">Haute</option>
                    <option value="urgent">Urgente</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Date limite</label>
                  <input
                    type="datetime-local"
                    value={taskFormData.deadline}
                    onChange={e => setTaskFormData({ ...taskFormData, deadline: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  />
                </div>
              </div>
              <div className="flex items-center gap-4 pt-4 border-t border-gray-200">
                <button
                  type="submit"
                  className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  Create task
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowCreateTaskModal(false);
                    setError(null);
                  }}
                  className="flex-1 px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
                >
                  Annuler
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Time Estimation Modal */}
      {showEstimationModal && selectedTaskForEstimation && timeEstimationVotes[selectedTaskForEstimation._id] && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b px-6 py-4 flex items-center justify-between">
              <h3 className="text-xl font-semibold">Estimation de temps pour "{selectedTaskForEstimation.title}"</h3>
              <button
                onClick={() => {
                  setShowEstimationModal(false);
                  setSelectedTaskForEstimation(null);
                  setSelectedEstimationOptions([]);
                  setCustomHours('');
                  setError(null);
                }}
                className="text-gray-500 hover:text-gray-700"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="p-6 space-y-4">
              {error && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg flex items-center gap-2">
                  <AlertCircle className="h-5 w-5" />
                  <span className="font-medium">{error}</span>
                </div>
              )}
              <p className="text-sm text-gray-600">Vote on the estimated time for this task. The average estimate will be applied.</p>
              
              {/* Options prédéfinies */}
              <div className="space-y-2 mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">Preset options:</label>
                {timeEstimationVotes[selectedTaskForEstimation._id]?.options.map((option, index) => (
                  <label
                    key={index}
                    className={`flex items-center gap-3 p-3 border-2 rounded-lg cursor-pointer transition-colors ${
                      selectedEstimationOptions.includes(index) && !customHours
                        ? 'border-blue-500 bg-blue-50'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <input
                      type="radio"
                      name="timeEstimationOption"
                      checked={selectedEstimationOptions.includes(index) && !customHours}
                      onChange={() => {
                        setSelectedEstimationOptions([index]);
                        setCustomHours('');
                      }}
                      className="h-4 w-4 text-blue-600"
                    />
                    <span className="flex-1">{option.text}</span>
                  </label>
                ))}
              </div>

              {/* Saisie manuelle */}
              <div className="border-t border-gray-200 pt-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">Ou saisir manuellement :</label>
                <div className="flex items-center gap-2">
                  <input
                    type="number"
                    min="0"
                    step="0.5"
                    value={customHours}
                    onChange={(e) => {
                      setCustomHours(e.target.value);
                      if (e.target.value) {
                        setSelectedEstimationOptions([]);
                      }
                    }}
                    placeholder="Nombre d'heures"
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                  <span className="text-sm text-gray-600">heures</span>
                </div>
                <p className="text-xs text-gray-500 mt-1">You can enter a decimal number (e.g. 2.5 for 2h30)</p>
              </div>
              <div className="flex items-center gap-4 pt-4 border-t border-gray-200">
                {timeEstimationVotes[selectedTaskForEstimation._id]?.userHasVoted ? (
                  <span className="flex-1 text-sm text-green-600 flex items-center justify-center gap-1 px-4 py-2 bg-green-50 rounded-lg border border-green-200">
                    <CheckCircle2 className="h-4 w-4" />
                    You voted
                  </span>
                ) : (
                  <button
                    onClick={submitTimeEstimationVote}
                    disabled={(selectedEstimationOptions.length === 0 && !customHours) || estimationLoading}
                    className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
                  >
                    {estimationLoading ? <Loader2 className="h-5 w-5 animate-spin mx-auto" /> : 'Soumettre l\'estimation'}
                  </button>
                )}
                {(() => {
                  const taskCreatorId = typeof selectedTaskForEstimation.createdBy === 'object'
                    ? selectedTaskForEstimation.createdBy?._id
                    : selectedTaskForEstimation.createdBy;
                  return taskCreatorId === user?._id && (
                    <button
                      onClick={applyTimeEstimation}
                      disabled={estimationLoading || timeEstimationVotes[selectedTaskForEstimation._id]?.results.totalVotes === 0}
                      className="flex-1 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
                    >
                      {estimationLoading ? <Loader2 className="h-5 w-5 animate-spin mx-auto" /> : 'Appliquer l\'estimation'}
                    </button>
                  );
                })()}
              </div>
              {timeEstimationVotes[selectedTaskForEstimation._id]?.status === 'closed' &&
               timeEstimationVotes[selectedTaskForEstimation._id]?.estimatedHours !== undefined &&
               timeEstimationVotes[selectedTaskForEstimation._id]?.estimatedHours !== null && (
                <div className="mt-4 pt-4 border-t border-gray-200 bg-blue-50 p-4 rounded-lg">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-blue-500 rounded-lg">
                      <Gauge className="h-5 w-5 text-white" />
                    </div>
                    <div className="flex-1">
                      <span className="text-xs font-semibold text-gray-600 uppercase tracking-wide">Estimation Finale</span>
                      <div className="flex items-center gap-2 mt-1">
                        <span className="text-lg font-bold text-gray-900">
                          {Math.ceil((timeEstimationVotes[selectedTaskForEstimation._id]?.estimatedHours || 0) / 8)} jour(s)
                          ({(timeEstimationVotes[selectedTaskForEstimation._id]?.estimatedHours || 0).toFixed(1)} heures)
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TeamProjectCollaborationPage;

